/**
 * CMMD control-plane HTTP client construction for sidebar project removal.
 *
 * Extracted from `sidebarProjectRemoval.ts`: the same-origin proxy URL
 * builders, the packaged-desktop cloud bootstrap lookup, and the
 * `CmmdForgeRuntimeApiClient` factory that every removal saga step
 * (`removeCloudProjectWithSaga`, `destroyCmmdRuntimeForEnvironment`,
 * `readLiveCloudProjectCatalog`) constructs a client through. This is plumbing
 * with no removal-decision logic of its own — nothing here decides WHAT gets
 * removed, only how the request reaches CMMD — so it moves as a self-contained
 * unit with no behaviour change.
 */
import type { DesktopEnvironmentBootstrapCloud, EnvironmentId } from "@cmmd-forge/contracts";
import { CmmdForgeRuntimeApiClient } from "~/environments/primary/runtimeConnectionClient";
import { resolveCmmdControlPlaneHttpUrl } from "~/lib/desktopCloudAuth";

export function parsePositiveInteger(value: string | null | undefined): number | undefined {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function cmmdRuntimeDestroyProxyUrl(baseUrl: string, runtimeId: string): string {
  return new URL(
    `/api/cmmd/forge-runtimes/${encodeURIComponent(runtimeId)}/destroy`,
    baseUrl,
  ).toString();
}

function cmmdCloudProjectRemovalProxyUrl(baseUrl: string, cmmdProjectId: number): string {
  return new URL(
    `/api/cmmd/forge-cloud-projects/${encodeURIComponent(String(cmmdProjectId))}/removal`,
    baseUrl,
  ).toString();
}

function cmmdCloudProjectRemovalPreviewProxyUrl(baseUrl: string, cmmdProjectId: number): string {
  return new URL(
    `/api/cmmd/forge-cloud-projects/${encodeURIComponent(String(cmmdProjectId))}/removal-preview`,
    baseUrl,
  ).toString();
}

function cmmdCloudProjectRemovalReceiptProxyUrl(baseUrl: string, operationId: string): string {
  return new URL(
    `/api/cmmd/forge-cloud-project-removals/${encodeURIComponent(operationId)}`,
    baseUrl,
  ).toString();
}

/**
 * The catalog READ the sidebar's cloud group renders from — the same
 * same-origin proxy `cmmdForgeRuntimeListResource` uses. Needed here so a
 * removal can resolve a registration id from the catalog ITSELF rather than
 * only from the cached in-memory snapshot, which is empty on a cold boot.
 */
function cmmdCloudProjectCatalogProxyUrl(baseUrl: string): string {
  return new URL("/api/cmmd/forge-cloud-projects", baseUrl).toString();
}

/**
 * The packaged-desktop cloud bootstrap that carries the local owner-session
 * bridge. The top-level bearer is a runtime ticket and must never authenticate
 * an owner control-plane mutation.
 *
 * The nested desktop-local owner session is intentionally NOT matched to the
 * bootstrap's current runtime. One desktop session can remove an unprovisioned
 * catalog row or another cloud member represented by the same logical sidebar
 * row; neither target is required to be the currently connected Sprite.
 */
export function readDesktopCloudBootstrap(): DesktopEnvironmentBootstrapCloud | null {
  const bootstrap = window.desktopBridge?.getLocalEnvironmentBootstrap?.() ?? null;
  if (!bootstrap || bootstrap.primaryMode !== "cloud") return null;
  return bootstrap;
}

export function desktopCloudBootstrapMatchesRuntime(
  bootstrap: DesktopEnvironmentBootstrapCloud,
  environmentId: EnvironmentId,
  runtimeId: string | null,
): boolean {
  return bootstrap.runtimeId === runtimeId || bootstrap.environmentId === environmentId;
}

export function createCmmdControlPlaneClient(
  bootstrap: DesktopEnvironmentBootstrapCloud | null,
): CmmdForgeRuntimeApiClient {
  const proxyBaseUrl = bootstrap?.desktopLocal?.httpBaseUrl ?? window.location.origin;
  const ownerSessionToken = bootstrap?.desktopLocal?.bearerToken;
  if (bootstrap && (!bootstrap.desktopLocal?.httpBaseUrl || !ownerSessionToken)) {
    throw new Error(
      "Forge Desktop cannot remove this cloud project until its local owner session is restored.",
    );
  }
  return new CmmdForgeRuntimeApiClient({
    cmmdHttpBaseUrl: resolveCmmdControlPlaneHttpUrl(),
    ...(ownerSessionToken ? { accessToken: ownerSessionToken } : {}),
    listUrl: cmmdCloudProjectCatalogProxyUrl(proxyBaseUrl),
    destroyUrl: (runtimeId) => cmmdRuntimeDestroyProxyUrl(proxyBaseUrl, runtimeId),
    cloudProjectRemovalUrl: (projectId) => cmmdCloudProjectRemovalProxyUrl(proxyBaseUrl, projectId),
    cloudProjectRemovalPreviewUrl: (projectId) =>
      cmmdCloudProjectRemovalPreviewProxyUrl(proxyBaseUrl, projectId),
    cloudProjectRemovalReceiptUrl: (operationId) =>
      cmmdCloudProjectRemovalReceiptProxyUrl(proxyBaseUrl, operationId),
  });
}
